// lib/bands — a server-banded list's BAND STATE as pure rules (ADR-078 §4 ·
// ADR-040 §6), shared by every paginated list that groups on the server:
//
//   · THE OPEN SET — the bands a person opened, as the URL holds it (`open=`,
//     written REPLACE: a disclosure refines the view, Back never replays it;
//     an ownKey of the perspective, saved with a view): read tolerantly, a band
//     opened jumps to the page where it starts in the stream, a band folded
//     lands on its header's own page, a criterion change resets it;
//   · THE FRAME — the criterion, the open set and the offset an ANSWER was
//     asked in: the page renders the answer on screen in the frame that
//     produced it, never the URL's, which may already say otherwise while the
//     next answer comes;
//   · THE STREAM — a view's band half over those two (`bandStream`): what the
//     table draws open, the four acts with their page moves, the dataset's
//     size, the table's `serverBands` prop.
//
// A page keeps what is its own: its URL keys (it maps `BandWrite` onto them),
// its params, its query, which keys an axis admits, a band's words.
// Pure, so every rule is a unit test beside it (FRONTEND-PATTERNS §Group-by).

import type { ReactNode } from 'react';
import { BAND_ALL, BandKeySchema, type Band } from '@ems-app/schemas/common/bands';
import type { ServerBandsProps } from '../components/ui/DataTable.js';

// ── the open set ─────────────────────────────────────────────────────────────

/** The opened bands' keys, or every band (`*`); an empty set = folded, how a grouped view arrives. */
export type OpenSet = ReadonlySet<string> | '*';

/** The open set as the URL holds it, READ TOLERANTLY like every slot (ADR-037): a key the wire refuses is dropped,
 *  never a 400 — and so is a key the axis does not admit (`admits`: a ladder keeps only its own rungs; absent = any
 *  key, a stale id being the server's tolerant read). */
export const openSetOf = (tokens: readonly string[], admits?: (key: string) => boolean): OpenSet => {
  // A token is read as the wire reads it — the space around it is not part of the key.
  const keys = tokens.map((k) => k.trim());
  return keys.includes(BAND_ALL) ? '*' : new Set(keys.filter((k) => BandKeySchema.safeParse(k).success && (!admits || admits(k))));
};
export const isOpen = (open: OpenSet, key: string): boolean => open === '*' || open.has(key);
/** The open set as the URL and the wire carry it. */
export const openTokens = (open: OpenSet): string[] => (open === '*' ? [BAND_ALL] : [...open]);

/** The stream page a band STARTS on — the opened bands' counts before it over the page size (the last answer's
 *  counts — exact: the server bounds the stream to the rows it counted). */
export function bandPage(open: OpenSet, bands: readonly Band[], key: string, pageSize: number): number {
  let pos = 0;
  for (const b of bands) {
    if (b.key === key) break;
    if (isOpen(open, b.key)) pos += b.count;
  }
  return Math.floor(pos / pageSize) + 1;
}

/** A band opened: joins the open set; the page jumps to where the band starts — clamped to the stream's end (a band of
 *  nothing opened past the last page lands on that page). */
export function openBand(open: OpenSet, bands: readonly Band[], key: string, pageSize: number): { open: string[]; page: number } {
  const next: OpenSet = open === '*' ? '*' : new Set([...open, key]);
  const total = bands.reduce((n, b) => n + (isOpen(next, b.key) ? b.count : 0), 0);
  return { open: openTokens(next), page: Math.max(1, Math.min(bandPage(next, bands, key, pageSize), Math.ceil(total / pageSize))) };
}

/** The open set the TABLE draws while the next answer is in flight: the answer's own (its rows are on screen), joined by
 *  the keys the URL opened since — a pressed header expands at once, empty, while its rows come (ADR-040 §6); a key the
 *  URL folded keeps its rows until the answer without them lands. */
export function unionOpen(shown: OpenSet, url: OpenSet): OpenSet {
  return shown === '*' || url === '*' ? '*' : new Set([...shown, ...url]);
}

/** A band folded: leaves the open set (under `*` the others are named); its rows leave the stream and the person
 *  lands on the folded header's OWN page — the page the band starts on: this page when it started here (nothing
 *  moves), the earlier page when the fold came from a "continued" header (the header stays in view instead of the
 *  next band's middle) — clamped to the stream's new end. */
export function foldBand(open: OpenSet, bands: readonly Band[], key: string, page: number, pageSize: number): { open: string[] | null; page: number } {
  const next = (open === '*' ? bands.map((b) => b.key) : [...open]).filter((k) => k !== key);
  const total = bands.reduce((n, b) => n + (next.includes(b.key) ? b.count : 0), 0);
  const landing = Math.min(page, bandPage(new Set(next), bands, key, pageSize));
  return { open: next.length ? next : null, page: Math.max(1, Math.min(landing, Math.ceil(total / pageSize))) };
}

/** The gear's two actions: every band open (the whole list, cut) · every band folded (the overview). */
export const openAll = (): { open: string[]; page: 1 } => ({ open: [BAND_ALL], page: 1 });
export const foldAll = (): { open: null; page: 1 } => ({ open: null, page: 1 });

/** The criterion changes (or leaves): the open set and the page go with it — a band's key means nothing on another axis. */
export const setGroup = (value: string): { group: string | null; open: null; page: 1 } => ({ group: value || null, open: null, page: 1 });

// ── the frame ────────────────────────────────────────────────────────────────

/** The FRAME an answer was asked in — the criterion, the open set and the offset of ITS request. While a newer request
 *  is in flight the previous answer stays on screen (placeholder data) and the URL already says otherwise: the bands,
 *  their open set and the rows' positions must agree with the rows, never with the URL (ADR-078 §4 · ADR-075 §4). The
 *  frame travels with the answer, so the placeholder carries its own. */
export interface ListFrame<A extends string = string> {
  criterion: A | null;
  open: readonly string[];
  offset: number;
}

/** Stamps an answer with the frame of its request — a list hook's last step (`.then(framed(params))`). */
export const framed =
  <A extends string>(params: { group_by?: A; open?: readonly string[]; offset: number }) =>
  <R extends object>(answer: R): R & { frame: ListFrame<A> } => ({ ...answer, frame: { criterion: params.group_by ?? null, open: params.open ?? [], offset: params.offset } });

// ── the stream — a view's band half ──────────────────────────────────────────

/** What a band act writes — the page maps it onto its own URL keys (null clears a key). */
export interface BandWrite {
  group?: string | null;
  open?: string[] | null;
  page?: number;
}

export interface BandStreamInput<A extends string> {
  /** The URL's criterion, read tolerantly by the page; null = ungrouped. */
  criterion: A | null;
  /** The URL's open set, read by the page on that criterion (`openSetOf`, with what the axis admits); null when ungrouped. */
  open: OpenSet | null;
  /** The URL's page and the list's page size. */
  page: number;
  pageSize: number;
  /** The answer on screen: the frame it was asked in and its bands (null · none before the first answer). */
  frame: ListFrame<A> | null;
  bands: readonly Band[];
  /** Writes the URL (replace) — the page's mapping of `BandWrite` onto its keys. */
  write: (patch: BandWrite) => void;
  /** The landing gesture — a move to another page of the stream lands at the working top. */
  land: () => void;
}

export interface BandStream<A extends string> {
  /** The answer's frame — the criterion and the offset the table renders in. */
  shown: ListFrame<A> | null;
  /** The answer's open set: the counts and the pager read this one alone. */
  shownOpen: OpenSet | null;
  /** What the table DRAWS open: the answer's set, joined by the keys the URL opened since on the same axis. */
  tableOpen: OpenSet | null;
  /** The page the answer on screen was asked for — what a fold or a jump is measured against. */
  shownPage: number;
  /** The DATASET's size — every band's count summed (the stream's `total` is only the opened bands'); null when the
   *  answer holds no band. */
  population: number | null;
  /** The criterion changes (or leaves, `''`): the open set and the page go with it. */
  setGroup: (value: string) => void;
  /** A header pressed: opening jumps to the band's page, folding lands on its header's own page. A press on the
   *  previous answer's bands — another axis still on screen while the new answer comes — writes nothing. */
  toggleBand: (key: string, on: boolean) => void;
  openAll: () => void;
  foldAll: () => void;
  /** The table's server-band prop — the surface gives a row's band and a band's words; undefined while the answer on
   *  screen is not grouped. */
  table: <T>(bandOf: (row: T) => string, renderLabel: (band: Band, criterion: A) => ReactNode) => ServerBandsProps<T> | undefined;
}

/** A view's band half, from what the URL holds and the answer on screen. */
export function bandStream<A extends string>({ criterion, open, page, pageSize, frame, bands, write, land }: BandStreamInput<A>): BandStream<A> {
  const shown = frame;
  // The frame's tokens are the ones the page SENT — already read on their axis; the counts and the stream agree on them.
  const shownOpen: OpenSet | null = shown?.criterion ? openSetOf(shown.open) : null;
  const tableOpen: OpenSet | null = shownOpen && open && shown?.criterion === criterion ? unionOpen(shownOpen, open) : shownOpen;
  const shownPage = shown ? Math.floor(shown.offset / pageSize) + 1 : page;
  const toggleBand = (key: string, on: boolean): void => {
    if (!open || !shown || shown.criterion !== criterion) return;
    const next = on ? openBand(open, bands, key, pageSize) : foldBand(open, bands, key, shownPage, pageSize);
    if (next.page !== page) land();
    write(next);
  };
  return {
    shown,
    shownOpen,
    tableOpen,
    shownPage,
    population: bands.length ? bands.reduce((n, b) => n + b.count, 0) : null,
    // A new view — landed at the working top like every change of the question.
    setGroup: (value) => {
      land();
      write(setGroup(value));
    },
    toggleBand,
    openAll: () => {
      land();
      write(openAll());
    },
    foldAll: () => {
      land();
      write(foldAll());
    },
    table: (bandOf, renderLabel) => {
      const axis = shown?.criterion;
      return axis && tableOpen ? { bands, open: tableOpen, bandOf, onToggle: toggleBand, renderLabel: (band) => renderLabel(band, axis), page: { offset: shown.offset, size: pageSize } } : undefined;
    },
  };
}
